// Admin Module: Team Assignment
// Team and responsibility assignment management

import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
} from "@mui/material";
import {
    Assignment,
    Group,
    Person,
    Edit,
} from "@mui/icons-material";
import { opportunitiesService } from "../../opportunities/opportunitiesService";

const TeamAssignment = () => {
    // const { t } = useTranslation(); // Removed unused
    // const { roleService } = useRole(); // Removed unused
    const [opportunities, setOpportunities] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
  
    // Assignment dialog state
    const [assignmentDialog, setAssignmentDialog] = useState({
        open: false,
        opportunity: null,
        assignments: {
            agent: "",
            legal: "",
            reviewer: "",
            analyst: "",
        },
    });

    // Team definitions
    const teams = {
        INVESTMENT: {
            name: "Equipo de Gestión de Oportunidades y Análisis de Inversión",
            description: "Evalúa el potencial de la oportunidad, realiza análisis financiero y decide sobre viabilidad",
            color: "primary",
        },
        CONSTRUCTION: {
            name: "Equipo de Análisis de Reformas y Obra Nueva",
            description: "Evalúa reformas necesarias y proceso de construcción o promoción",
            color: "secondary",
        },
        LEGAL: {
            name: "Equipo Legal",
            description: "Revisa legalidad de la oportunidad, validación de contratos y cumplimiento normativo",
            color: "warning",
        },
        FINAL: {
            name: "Responsable Final de Validación",
            description: "Revisa y aprueba cada fase del proceso",
            color: "success",
        },
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [oppsData, agentsData] = await Promise.all([
                opportunitiesService.getActiveOpportunities()
                // TODO(api): Replace agentService usage with API call
                // agentService.listAgents()
            ]);
            setOpportunities(oppsData);
            setAgents(agentsData);
        } catch (error) {
            setError("Error al cargar los datos");
            // Error loading data
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAssignment = (opportunity) => {
        setAssignmentDialog({
            open: true,
            opportunity,
            assignments: {
                agent: opportunity.agentId || "",
                legal: opportunity.legalTeamId || "",
                reviewer: opportunity.reviewerId || "",
                analyst: opportunity.analystId || "",
            },
        });
    };

    const handleAssignmentChange = (team, value) => {
        setAssignmentDialog(prev => ({
            ...prev,
            assignments: {
                ...prev.assignments,
                [team]: value,
            },
        }));
    };

    const confirmAssignments = async () => {
        try {
            const { opportunity, assignments } = assignmentDialog;
      
            await opportunitiesService.updateOpportunity(opportunity.id, {
                agentId: assignments.agent,
                legalTeamId: assignments.legal,
                reviewerId: assignments.reviewer,
                analystId: assignments.analyst,
                assignedAt: new Date(),
            });

            setAssignmentDialog({ open: false, opportunity: null, assignments: {} });
            loadData();
        } catch (error) {
            setError("Error al asignar equipos");
            // Error assigning teams
        }
    };

    const getAgentById = (agentId) => {
        return agents.find(agent => agent.id === agentId);
    };

    const getAssignmentStatus = (opportunity) => {
        const hasAgent = !!opportunity.agentId;
        const hasLegal = !!opportunity.legalTeamId;
        const hasReviewer = !!opportunity.reviewerId;
        const hasAnalyst = !!opportunity.analystId;
    
        const total = 4;
        const assigned = [hasAgent, hasLegal, hasReviewer, hasAnalyst].filter(Boolean).length;
    
        if (assigned === 0) return { status: "unassigned", color: "error", percentage: 0 };
        if (assigned === total) return { status: "complete", color: "success", percentage: 100 };
        if (assigned >= total * 0.75) return { status: "almost", color: "warning", percentage: 75 };
        return { status: "partial", color: "info", percentage: 50 };
    };

    // Defensive: ensure agents is always an array
    const safeAgents = Array.isArray(agents) ? agents : [];
    const getTeamMembers = (teamType) => {
        // Filter agents by team type
        return safeAgents.filter(agent => {
            switch (teamType) {
            case "agent":
                return agent.tipo === "captador";
            case "legal":
                return agent.tipo === "legal";
            case "reviewer":
                return agent.tipo === "revisor";
            case "analyst":
                return agent.tipo === "analista";
            default:
                return true;
            }
        });
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography>Cargando asignaciones...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Team Overview */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {Object.entries(teams).map(([key, team]) => (
                    <Grid item xs={12} md={6} lg={3} key={key}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <Group color={team.color} sx={{ mr: 1 }} />
                                    <Typography variant="h6" color={`${team.color}.main`}>
                                        {team.name}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    {team.description}
                                </Typography>
                                <Chip
                                    label={`${getTeamMembers(key.toLowerCase()).length} miembros`}
                                    color={team.color}
                                    size="small"
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Assignment Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <Assignment />
            Asignación de Equipos por Oportunidad
                    </Typography>
          
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Oportunidad</TableCell>
                                    <TableCell>Agente Captador</TableCell>
                                    <TableCell>Equipo Legal</TableCell>
                                    <TableCell>Revisor</TableCell>
                                    <TableCell>Analista</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {opportunities.map((opportunity) => {
                                    const assignmentStatus = getAssignmentStatus(opportunity);
                                    return (
                                        <TableRow key={opportunity.id} hover>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                                        {opportunity.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {opportunity.location}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                {opportunity.agentId ? (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Avatar sx={{ width: 24, height: 24 }}>
                                                            <Person />
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {getAgentById(opportunity.agentId)?.nombre || "N/A"}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Sin asignar" color="error" size="small" />
                                                )}
                                            </TableCell>
                      
                                            <TableCell>
                                                {opportunity.legalTeamId ? (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Avatar sx={{ width: 24, height: 24 }}>
                                                            <Person />
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {getAgentById(opportunity.legalTeamId)?.nombre || "N/A"}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Sin asignar" color="error" size="small" />
                                                )}
                                            </TableCell>
                      
                                            <TableCell>
                                                {opportunity.reviewerId ? (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Avatar sx={{ width: 24, height: 24 }}>
                                                            <Person />
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {getAgentById(opportunity.reviewerId)?.nombre || "N/A"}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Sin asignar" color="error" size="small" />
                                                )}
                                            </TableCell>
                      
                                            <TableCell>
                                                {opportunity.analystId ? (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Avatar sx={{ width: 24, height: 24 }}>
                                                            <Person />
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {getAgentById(opportunity.analystId)?.nombre || "N/A"}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Sin asignar" color="error" size="small" />
                                                )}
                                            </TableCell>
                      
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Box sx={{ width: 60, bgcolor: "grey.200", borderRadius: 1, overflow: "hidden" }}>
                                                        <Box
                                                            sx={{
                                                                width: `${assignmentStatus.percentage}%`,
                                                                height: 8,
                                                                bgcolor: `${assignmentStatus.color}.main`,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Chip
                                                        label={`${assignmentStatus.percentage}%`}
                                                        color={assignmentStatus.color}
                                                        size="small"
                                                    />
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Tooltip title="Gestionar asignaciones">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenAssignment(opportunity)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Assignment Dialog */}
            <Dialog
                open={assignmentDialog.open}
                onClose={() => setAssignmentDialog({ open: false, opportunity: null, assignments: {} })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
          Asignar Equipos - {assignmentDialog.opportunity?.title}
                </DialogTitle>
                <DialogContent>
                    {assignmentDialog.opportunity && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Agente Captador</InputLabel>
                                    <Select
                                        value={assignmentDialog.assignments.agent}
                                        onChange={(e) => handleAssignmentChange("agent", e.target.value)}
                                    >
                                        <MenuItem value="">Sin asignar</MenuItem>
                                        {getTeamMembers("agent").map(agent => (
                                            <MenuItem key={agent.id} value={agent.id}>
                                                {agent.nombre} ({agent.tipo})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
              
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Equipo Legal</InputLabel>
                                    <Select
                                        value={assignmentDialog.assignments.legal}
                                        onChange={(e) => handleAssignmentChange("legal", e.target.value)}
                                    >
                                        <MenuItem value="">Sin asignar</MenuItem>
                                        {getTeamMembers("legal").map(agent => (
                                            <MenuItem key={agent.id} value={agent.id}>
                                                {agent.nombre} ({agent.tipo})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
              
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Revisor</InputLabel>
                                    <Select
                                        value={assignmentDialog.assignments.reviewer}
                                        onChange={(e) => handleAssignmentChange("reviewer", e.target.value)}
                                    >
                                        <MenuItem value="">Sin asignar</MenuItem>
                                        {getTeamMembers("reviewer").map(agent => (
                                            <MenuItem key={agent.id} value={agent.id}>
                                                {agent.nombre} ({agent.tipo})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
              
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Analista</InputLabel>
                                    <Select
                                        value={assignmentDialog.assignments.analyst}
                                        onChange={(e) => handleAssignmentChange("analyst", e.target.value)}
                                    >
                                        <MenuItem value="">Sin asignar</MenuItem>
                                        {getTeamMembers("analyst").map(agent => (
                                            <MenuItem key={agent.id} value={agent.id}>
                                                {agent.nombre} ({agent.tipo})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignmentDialog({ open: false, opportunity: null, assignments: {} })}>
            Cancelar
                    </Button>
                    <Button onClick={confirmAssignments} variant="contained">
            Confirmar Asignaciones
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TeamAssignment; 